/**
 * Google Identity Services (GIS) wrapper — the browser half of staff sign-in
 * (Task 4092 step 1).
 *
 * The desktop app gets its Google ID token from the Rust layer, which runs a
 * full OAuth code flow and persists a refresh token in the macOS Keychain.
 * A browser SPA cannot do either: there is no Keychain, and a refresh token
 * must never reach JavaScript. GIS is Google's supported answer — it returns a
 * short-lived ID token from a hidden iframe or a popup, with the refresh
 * relationship held by Google's own session cookie rather than by us.
 *
 * We load the GIS script on demand rather than adding a dependency: Google
 * explicitly requires it be served from their CDN so the client picks up
 * security fixes, and it is not distributed on npm.
 *
 * ID tokens expire in ~1 hour. GIS does NOT silently renew the way the Firebase
 * SDK did, so callers must re-request before expiry — that is Task 4092 step 4,
 * deliberately not solved here.
 */

const GIS_SRC = 'https://accounts.google.com/gsi/client'

/**
 * Bound on loading Google's script. A dropped (not rejected) connection would
 * otherwise stall until the browser's own ~300s connection timeout.
 */
const GIS_LOAD_TIMEOUT_MS = 8000
const HOSTED_DOMAIN = 'kodeon.ai'

/** The OAuth Web client ID, inlined by Vite at build time. Public by design. */
export const GOOGLE_OAUTH_CLIENT_ID = (
  import.meta.env?.VITE_GOOGLE_OAUTH_CLIENT_ID ?? ''
).toString()

/** Shape of the credential response GIS hands back. */
interface CredentialResponse {
  credential?: string
}

interface GoogleIdApi {
  initialize(config: {
    client_id: string
    callback: (response: CredentialResponse) => void
    hosted_domain?: string
    auto_select?: boolean
    cancel_on_tap_outside?: boolean
    use_fedcm_for_prompt?: boolean
  }): void
  prompt(listener?: (notification: PromptNotification) => void): void
  renderButton(parent: HTMLElement, options: GsiButtonOptions): void
  cancel(): void
  disableAutoSelect(): void
}

/** Options for the Google-rendered sign-in button. */
export interface GsiButtonOptions {
  type?: 'standard' | 'icon'
  theme?: 'outline' | 'filled_blue' | 'filled_black'
  size?: 'small' | 'medium' | 'large'
  text?: 'signin_with' | 'signup_with' | 'continue_with' | 'signin'
  shape?: 'rectangular' | 'pill' | 'circle' | 'square'
  width?: number
  logo_alignment?: 'left' | 'center'
}

interface PromptNotification {
  isNotDisplayed?: () => boolean
  isSkippedMoment?: () => boolean
  isDismissedMoment?: () => boolean
  getNotDisplayedReason?: () => string
  getSkippedReason?: () => string
  getDismissedReason?: () => string
}

declare global {
  interface Window {
    google?: { accounts?: { id?: GoogleIdApi } }
  }
}

let scriptPromise: Promise<GoogleIdApi> | null = null

/**
 * The single credential waiter (PR #111 B2).
 *
 * `google.accounts.id` is a process-wide singleton and Google's reference is
 * explicit: "If you do call the initialize method multiple times, only the
 * configurations in the last call is remembered and used." Each entry point
 * installing its own `callback` closure therefore means whoever initialised
 * LAST owns the credential, and the earlier promise can never resolve — a
 * refresh firing while the sign-in gate is mounted would silently steal the
 * operator's click.
 *
 * So initialize() is called once, with one dispatching callback that hands the
 * credential to whichever waiter is currently registered.
 */
type WaiterKind = 'interactive' | 'silent'

let pendingWaiter: {
  kind: WaiterKind
  resolve: (credential: string) => void
  reject: (err: Error) => void
} | null = null

let initialised = false

/**
 * Register the sole waiter.
 *
 * A SILENT waiter (a background 401 refresh) will not displace an INTERACTIVE
 * one (a mounted sign-in gate). Priority matters here (PR #111 B3): the gate
 * renders Google's button exactly once and has no way to re-arm, so letting a
 * refresh displace it left a button that looked clickable but dropped the
 * operator's credential on the floor — permanently, with no error. That is the
 * same symptom B2 was filed for, reached by a new route.
 *
 * An operator sitting at the gate is more important than a background refresh,
 * so the refresh is the one that yields.
 */
function setWaiter(
  kind: WaiterKind,
  resolve: (credential: string) => void,
  reject: (err: Error) => void,
): boolean {
  if (kind === 'silent' && pendingWaiter?.kind === 'interactive') {
    reject(new GisNotDisplayedError('gate_active'))
    return false
  }
  pendingWaiter?.reject(new Error('Superseded by a newer sign-in attempt'))
  pendingWaiter = { kind, resolve, reject }
  return true
}

function settleWaiter(fn: (w: NonNullable<typeof pendingWaiter>) => void): void {
  const waiter = pendingWaiter
  if (!waiter) {
    // A real credential can land here — FedCM latency, or the up-to-60s
    // delayed skipped-moment window. Silently dropping it leaves an operator
    // with a button that "does nothing" and no way to explain it (PR #111 NB1).
    console.warn('[gis] credential or notification arrived with no waiter registered')
    return
  }
  pendingWaiter = null
  fn(waiter)
}

/**
 * Initialise the GIS singleton exactly once.
 *
 * `auto_select` is fixed to true here rather than varied per call — varying it
 * is what forced the multiple-initialize pattern this replaces. It only affects
 * One Tap, which is now used solely for the SILENT refresh (`requestIdToken`).
 * Without it One Tap always waits for a click, so the "silent" refresh could
 * never return a credential and every token expiry signed the operator out.
 * The interactive path (`renderButton`) ignores it. Sign-out calls
 * `disableAutoSelect()` so the next load does not silently sign straight back in.
 */
function ensureInitialised(api: GoogleIdApi): void {
  if (initialised) return
  api.initialize({
    client_id: GOOGLE_OAUTH_CLIENT_ID,
    hosted_domain: HOSTED_DOMAIN,
    auto_select: true,
    cancel_on_tap_outside: false,
    callback: (response) => {
      settleWaiter((w) => {
        if (response.credential) {
          w.resolve(response.credential)
        } else {
          w.reject(new Error('Google returned no credential'))
        }
      })
    },
  })
  initialised = true
}

/**
 * Load the GIS script once and resolve with `google.accounts.id`.
 * Concurrent callers share the same promise; a failed load is not cached, so a
 * later attempt can retry after a transient network failure.
 */
export function loadGis(): Promise<GoogleIdApi> {
  if (scriptPromise) return scriptPromise

  scriptPromise = new Promise<GoogleIdApi>((resolve, reject) => {
    const existing = window.google?.accounts?.id
    if (existing) {
      resolve(existing)
      return
    }

    const script = document.createElement('script')
    script.src = GIS_SRC
    script.async = true
    script.defer = true

    // Bound the script load (PR #111 B1 residual). onload/onerror are the only
    // things that settle this promise, and a proxy that DROPS rather than
    // rejects leaves the socket in SYN_SENT until the browser's own connection
    // timeout — ~300s in Chrome. During that window requestIdToken() never even
    // reaches the code that arms its 4s timer, so boot hangs on the splash and
    // refreshCredential()'s inFlight wedges every concurrent 401.
    //
    // Rejecting here also covers renderSignInButton(), which awaits loadGis()
    // with no timeout of its own — without this, a blocked CDN gives the
    // operator a silent blank gate, the exact case N9 exists to prevent.
    const loadTimer = setTimeout(() => {
      script.onload = null
      script.onerror = null
      script.remove()
      scriptPromise = null // stay retryable
      reject(new Error('Timed out loading Google Identity Services'))
    }, GIS_LOAD_TIMEOUT_MS)

    const settleLoad = (fn: () => void) => {
      clearTimeout(loadTimer)
      fn()
    }

    script.onload = () => {
      const api = window.google?.accounts?.id
      settleLoad(() => {
        if (api) {
          resolve(api)
        } else {
          reject(new Error('Google Identity Services loaded but exposed no API'))
        }
      })
    }
    script.onerror = () =>
      settleLoad(() => reject(new Error('Failed to load Google Identity Services')))
    document.head.appendChild(script)
  }).catch((err: unknown) => {
    // Do not cache a rejection — a transient CDN failure should be retryable.
    scriptPromise = null
    throw err
  })

  return scriptPromise
}

export class GisNotDisplayedError extends Error {
  constructor(reason: string) {
    super(`Google sign-in prompt was not displayed (${reason})`)
    this.name = 'GisNotDisplayedError'
  }
}

/**
 * Attempt a non-interactive One Tap sign-in.
 *
 * Silent only: interactive sign-in goes through `renderSignInButton`, because
 * `prompt()` cannot be driven from a click (see that function). The `silent`
 * parameter this used to take is gone — `auto_select` is a per-initialize
 * setting, and varying it per call is what forced the multiple-initialize
 * pattern that PR #111 B2 replaced.
 *
 * `hosted_domain` restricts the chooser to kodeon.ai. That is a UX hint only —
 * the gateway independently enforces the `hd` claim (`staff_google_auth.go`),
 * which is the actual boundary.
 *
 * Rejects rather than hangs: see SILENT_PROMPT_TIMEOUT_MS.
 */
/**
 * Timeout for a silent One Tap attempt (PR #111 B1).
 *
 * Under FedCM — mandatory since August 2025 — `prompt()` no longer emits
 * not-displayed notifications at all, and skipped moments are delayed by up to
 * a minute. With Chrome's built-in credential manager there is no notification
 * for display, skipped, OR dismissed moments, because they happen outside the
 * GIS library.
 *
 * The notification callback was the ONLY thing that could settle this promise.
 * Under FedCM it frequently never fires, so a browser with no active Google
 * session would hang on the splash screen forever and never reach the sign-in
 * gate. A wall-clock timeout is the one guarantee that does not depend on
 * Google calling us back, and it stays correct whatever Google changes next.
 */
const SILENT_PROMPT_TIMEOUT_MS = 4000

export async function requestIdToken(): Promise<string> {
  if (!GOOGLE_OAUTH_CLIENT_ID) {
    throw new Error(
      'VITE_GOOGLE_OAUTH_CLIENT_ID is not set — the browser build cannot sign in',
    )
  }

  const api = await loadGis()
  ensureInitialised(api)

  // Dismiss any prompt still on screen before starting another.
  api.cancel()

  return new Promise<string>((resolve, reject) => {
    let timer: ReturnType<typeof setTimeout> | undefined
    const done = (fn: () => void) => {
      if (timer !== undefined) clearTimeout(timer)
      fn()
    }

    const armed = setWaiter(
      'silent',
      (credential) => done(() => resolve(credential)),
      (err) => done(() => reject(err)),
    )
    // Refused because the sign-in gate owns the flow — reject() already ran.
    if (!armed) return

    // See SILENT_PROMPT_TIMEOUT_MS: under FedCM the notification below may
    // never fire, so this is the real guarantee against a hung boot.
    timer = setTimeout(() => {
      settleWaiter((w) => w.reject(new GisNotDisplayedError('timeout')))
    }, SILENT_PROMPT_TIMEOUT_MS)

    api.prompt((notification) => {
      // isDismissedMoment / getDismissedReason are the ONE notification pair
      // FedCM leaves fully intact. `credential_returned` is success, and
      // `flow_restarted` means a credential may still arrive — neither is a
      // failure. Only an explicit cancel is.
      //
      // The isNotDisplayed / isSkippedMoment branches below are legacy: FedCM
      // removed the former entirely and made the latter reason-less and delayed
      // by up to a minute. They are kept only for non-FedCM fallbacks; do not
      // rely on them, and do not re-derive confidence from their presence.
      if (notification?.isDismissedMoment?.()) {
        const reason = notification.getDismissedReason?.() ?? 'unknown'
        if (reason === 'credential_returned' || reason === 'flow_restarted') return
        settleWaiter((w) => w.reject(new GisNotDisplayedError(`dismissed: ${reason}`)))
        return
      }
      if (notification?.isNotDisplayed?.()) {
        const reason = notification.getNotDisplayedReason?.() ?? 'unknown'
        settleWaiter((w) => w.reject(new GisNotDisplayedError(reason)))
      } else if (notification?.isSkippedMoment?.()) {
        const reason = notification.getSkippedReason?.() ?? 'unknown'
        settleWaiter((w) => w.reject(new GisNotDisplayedError(reason)))
      }
    })
  })
}

/**
 * Render Google's own sign-in button into `parent` and resolve when the user
 * completes the flow.
 *
 * This is the SUPPORTED path for user-initiated sign-in. `prompt()` (One Tap)
 * cannot be driven from a click: it is designed to appear on its own, browsers
 * rate-limit and suppress it, and once it has been dismissed or exhausted it
 * returns `unknown_reason` with no way to force it. A custom button calling
 * prompt() therefore works until the user dismisses One Tap once, then fails
 * permanently for that session — which is exactly what we hit.
 *
 * The trade is cosmetic: this is Google's element, not ours, so it cannot carry
 * Stream's button styling. Google requires that for user-initiated sign-in.
 */
export async function renderSignInButton(
  parent: HTMLElement,
  options: GsiButtonOptions = {},
): Promise<string> {
  if (!GOOGLE_OAUTH_CLIENT_ID) {
    throw new Error(
      'VITE_GOOGLE_OAUTH_CLIENT_ID is not set — the browser build cannot sign in',
    )
  }

  const api = await loadGis()
  ensureInitialised(api)

  return new Promise<string>((resolve, reject) => {
    // Share the single dispatching callback rather than installing another
    // (PR #111 B2): a refresh firing while this button is mounted would
    // otherwise replace this promise's callback and steal the operator's click.
    setWaiter('interactive', resolve, reject)

    parent.innerHTML = ''
    api.renderButton(parent, {
      type: 'standard',
      theme: 'filled_blue',
      size: 'large',
      text: 'signin_with',
      shape: 'rectangular',
      logo_alignment: 'left',
      ...options,
    })
  })
}

/**
 * Test seam: drop the memoised script promise, the initialise latch, and any
 * pending waiter, so a suite can install a fresh fake singleton per test.
 */
export function resetGisStateForTests(): void {
  scriptPromise = null
  initialised = false
  pendingWaiter = null
}

/**
 * Dismiss any prompt currently on screen.
 *
 * The boot-time silent attempt leaves its card displayed when auto-select is
 * unavailable, which is fine on its own — but it must be cleared before the
 * sign-in gate renders its own button, or the two collide.
 */
export async function cancelPrompt(): Promise<void> {
  try {
    const api = await loadGis()
    api.cancel()
  } catch {
    // Best-effort.
  }
}

/** Clear GIS's auto-select state so the next sign-in shows the chooser. */
export async function disableAutoSelect(): Promise<void> {
  try {
    const api = await loadGis()
    api.disableAutoSelect()
  } catch {
    // Best-effort — never block sign-out on the CDN.
  }
}
